import { Alert, Breadcrumb, Card, Col, Form, Row, Spin, Table, Tree } from 'antd'
import { Button, ButtonActionBar, Typography, UploadModal } from 'modules/core/components'
import {
  PlusCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FolderOutlined,
  FileOutlined,
} from '@ant-design/icons'
import { useEffect, useState } from 'react'
import numeral from 'numeral'
import configuration from 'modules/configuration'
import { connect } from 'react-redux'
import upload from 'modules/uploadS3'
import login from 'modules/login'
import { CreateFolderModal, RecordModal } from './components'
import {
  addFolder,
  findNodeByKey,
  findPathByKey,
  generateTableData,
  handleDownload,
  onConfirmUpload,
  transformData,
} from './Utils'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import processes from 'modules/processes'
import { usePagination } from 'modules/core/customHooks'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

const { DirectoryTree } = Tree

const UploadS3 = ({
  fetchDirectoriesS3,
  direcatoriesList,
  uploadFileS3,
  uploadUrlAws,
  downloadFileS3,
  loggedUser,
  setAuditS3,
  fetchAuditList,
  auditList,
}) => {
  const enableOldS3 = useFeatureIsOn('enalbe-old-s3')
  const [selectedKeys, setSelectedKeys] = useState([])
  const [breadcrumbPath, setBreadcrumbPath] = useState([])
  const [expandedKeys, setExpandedKeys] = useState([])
  const [showModalCreate, setShowModalCreate] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [fileList, setFileList] = useState([])
  const [directoriesData, setDirectoriesData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [tablePagination, setTablePagination] = usePagination({
    page: 1,
    pageSize: 10,
  })
  const [showDrawer, setShowDrawer] = useState(false)
  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    fetchAuditList('s3', 'all', { ...tablePagination })
  }, [fetchAuditList, tablePagination])

  useEffect(() => {
    setIsLoading(true)
    fetchDirectoriesS3().then(() => setIsLoading(false))
  }, [fetchDirectoriesS3])

  useEffect(() => {
    const transformedData = transformData(direcatoriesList)
    setDirectoriesData(transformedData)
  }, [direcatoriesList])

  const onSelect = (keys, info) => {
    let keysAux = keys
    if (selectedKeys.includes(info.node.key)) {
      const aux = selectedKeys.filter((key) => key !== info.node.key)
      setSelectedKeys(aux)

      if (aux.length > 0) {
        const path = findPathByKey(directoriesData, aux[0])
        setBreadcrumbPath(path || [])
      } else {
        keysAux = []
        setBreadcrumbPath(keysAux)
      }
    } else {
      setSelectedKeys(keysAux)
    }

    if (keysAux.length > 0) {
      const path = findPathByKey(directoriesData, keysAux[0]).filter((el) => el.type !== 'file')
      setBreadcrumbPath(path || [])
    } else {
      setBreadcrumbPath([])
    }
  }

  const handleRowClick = (record) => {
    const nodeKey = record.key || record.id

    if (!selectedKeys.includes(nodeKey)) {
      setSelectedKeys([nodeKey])
      const path = findPathByKey(directoriesData, nodeKey)

      setBreadcrumbPath(path || [])
    }
  }

  const handleBreadcrumbClick = (node, index) => {
    const path = breadcrumbPath.slice(0, index + 1)
    const keysToExpand = path.map((n) => n.key)

    setSelectedKeys([node.key])
    setExpandedKeys(keysToExpand)
    setBreadcrumbPath(path)
  }

  const tableData = generateTableData(selectedKeys, directoriesData)
  const hayArchivos = tableData.some((item) => item.tipo === 'file')

  const baseColumns = [
    {
      title: '',
      key: 'icon',
      width: 20,
      render: (icon, record) => (record.tipo === 'folder' ? <FolderOutlined /> : <FileOutlined />),
    },
    {
      title: t('FIELD_NAME'),
      dataIndex: 'nombre',
      key: 'nombre',
      width: 250,
      render: (name, record) => {
        if (record.tipo === 'folder') {
          return (
            <div className="upload-moda-help">
              <span onClick={() => handleRowClick(record)}>{name}</span>
            </div>
          )
        } else {
          return name
        }
      },
    },
  ]

  const fileColumns = [
    {
      title: t('FIELD_DATE'),
      dataIndex: 'fecha',
      key: 'fecha',
    },
    {
      title: t('FIELD_SIZE'),
      dataIndex: 'tamaño',
      key: 'tamaño',
      render: (val) => val && numeral(val).format('0b'),
    },
    {
      title: t('FIELD_ACTIONS'),
      dataIndex: 'key',
      key: 'key',
      align: 'right',
      render: (key, record) => {
        return (
          record.tipo === 'file' && (
            <ButtonActionBar>
              <Button.Icon
                title="ACTION_DOWNLOAD"
                icon={
                  <Typography.Icon
                    icon={DownloadOutlined}
                    onClick={() =>
                      handleDownload({
                        key,
                        record,
                        directoriesData,
                        downloadFileS3,
                        loggedUser,
                        setAuditS3,
                        onRefetcAuditList: () =>
                          fetchAuditList('s3', 'all', { ...tablePagination }),
                      })
                    }
                  />
                }
              />
            </ButtonActionBar>
          )
        )
      },
    },
  ]

  const columns = hayArchivos ? [...baseColumns, ...fileColumns] : baseColumns

  const onAddUploadFile = (file) => {
    setFileList([file])
    return false
  }

  const onConfirm = () => {
    form.validateFields().then((values) => {
      const auxIndex =
        selectedKeys.length > 0 ? findNodeByKey(directoriesData, selectedKeys[0]) : { key: null }
      const newData = addFolder(directoriesData, auxIndex.key, values.name)
      setDirectoriesData(newData)
      setShowModalCreate(false)
      form.resetFields()
    })
  }

  return (
    <>
      <Spin spinning={isLoading}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Alert
              type="info"
              showIcon
              closable
              message={t('INFORMATIVE_ALERT_CREATE_FOLDER_MESSAGE')}
              description={t('INFORMATIVE_ALERT_CREATE_FOLDER_DESCRIPTION')}
            />
          </Col>
          <Col span={24}>
            <ButtonActionBar>
              <Button.Default title="LABEL_RECORD" onClick={() => setShowDrawer(true)} />
            </ButtonActionBar>
          </Col>
          <Col span={8}>
            <Card style={{ height: 400 }}>
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <ButtonActionBar position="space-between">
                    <Typography.Subtitle>{t('FIELD_FOLDERS')}</Typography.Subtitle>
                    <Button.LinkWhitLeftIcon
                      title="ACTION_CREATE"
                      icon={<Typography.Icon level={2} icon={PlusCircleOutlined} />}
                      onClick={() => setShowModalCreate(true)}
                    />
                  </ButtonActionBar>
                </Col>
                <Col
                  span={24}
                  style={{
                    height: '300px',
                    overflowY: 'auto',
                    overflowX: 'auto',
                  }}
                >
                  <DirectoryTree
                    multiple
                    defaultExpandAll
                    selectedKeys={selectedKeys}
                    onSelect={onSelect}
                    treeData={directoriesData}
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    expandAction="none"
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={16}>
            <Card style={{ height: 400 }}>
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <ButtonActionBar position="space-between">
                    <Breadcrumb
                      style={{
                        marginBottom: 16,
                        display: 'flex',
                        flexWrap: 'wrap',
                      }}
                    >
                      {breadcrumbPath.map((node, idx) => {
                        const isLast = idx === breadcrumbPath.length - 1
                        return (
                          <Breadcrumb.Item key={node.key}>
                            {isLast ? (
                              <span style={{ color: 'gray', cursor: 'default' }}>{node.title}</span>
                            ) : (
                              <a onClick={() => handleBreadcrumbClick(node, idx)}>{node.title}</a>
                            )}
                          </Breadcrumb.Item>
                        )
                      })}
                    </Breadcrumb>
                    <Button.LinkWhitLeftIcon
                      title="ACTION_UPLOAD"
                      icon={<Typography.Icon level={2} icon={UploadOutlined} />}
                      onClick={() => setShowUploadModal(true)}
                    />
                  </ButtonActionBar>
                </Col>
                <Col span={24}>
                  <Table
                    size="small"
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    scroll={{
                      y: 250,
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>
      <CreateFolderModal
        visible={showModalCreate}
        onClose={() => {
          form.resetFields()
          setShowModalCreate(false)
        }}
        onConfirm={onConfirm}
        form={form}
      />
      <UploadModal
        title={t('FIELD_FILE_S3_UPLOAD')}
        visible={showUploadModal}
        onCancel={() => {
          setShowUploadModal(false)
          setFileList([])
        }}
        onConfirm={() =>
          onConfirmUpload({
            setIsLoading: setIsSaving,
            breadcrumbPath,
            uploadFileS3,
            fileList,
            uploadUrlAws,
            onRefetch: () => fetchDirectoriesS3(),
            onClose: () => {
              setShowUploadModal(false)
              setFileList([])
              setIsSaving(false)
            },
            setAuditS3,
            onRefetcAuditList: () => fetchAuditList('s3', 'all', { ...tablePagination }),
            enableOldS3,
          })
        }
        isUploadingFile={isSaving}
        uploadProps={{
          name: 'file',
          accept: '.xls, .xlsx',
          showUploadList: { showPreviewIcon: false },
          beforeUpload: onAddUploadFile,
          onRemove: (file) => setFileList([]),
          fileList: fileList,
        }}
      />
      <RecordModal
        visible={showDrawer}
        onClose={() => setShowDrawer(false)}
        auditList={auditList}
        onChangeTable={setTablePagination}
        tablePagination={tablePagination}
      />
    </>
  )
}

const mapStateToProps = (state) => ({
  direcatoriesList: configuration.selectors.getDirecatoriesList(state),
  loggedUser: login.selectors.getWhoAmI(state),
  auditList: processes.selectors.getAuditList(state),
})

const mapDispatchToProps = {
  fetchDirectoriesS3: configuration.actions.fetchDirectoriesS3,
  uploadFileS3: configuration.actions.uploadFileS3,
  uploadUrlAws: upload.actions.uploadUrlAws,
  downloadFileS3: configuration.actions.downloadFileS3,
  setAuditS3: configuration.actions.setAuditS3,
  fetchAuditList: processes.actions.fetchAuditList,
}

UploadS3.propTypes = {
  fetchDirectoriesS3: PropTypes.func,
  direcatoriesList: PropTypes.array,
  uploadFileS3: PropTypes.func,
  uploadUrlAws: PropTypes.func,
  downloadFileS3: PropTypes.func,
  loggedUser: PropTypes.object,
  setAuditS3: PropTypes.func,
  fetchAuditList: PropTypes.func,
  auditList: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadS3)
